import React from 'react';
import { Link } from 'react-router-dom';
import { 
  SpeakerWaveIcon, 
  ClipboardDocumentListIcon, 
  HeartIcon,
  ChartBarIcon,
  UserGroupIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';

const Landing: React.FC = () => {
  const features = [
    {
      icon: SpeakerWaveIcon,
      title: 'Sound Assessment',
      description: 'Identify your tinnitus characteristics with our advanced audio testing tools.',
      link: '/assessment'
    },
    {
      icon: ClipboardDocumentListIcon,
      title: 'THI Questionnaire',
      description: 'Track your tinnitus impact with the standardized Tinnitus Handicap Inventory.',
      link: '/questionnaire'
    },
    {
      icon: HeartIcon,
      title: 'Treatment Hub',
      description: 'Explore evidence-based treatment options and bookmark your favorites.',
      link: '/treatments'
    }
  ];

  const benefits = [
    {
      icon: ChartBarIcon,
      title: 'Track Progress',
      description: 'Monitor changes in your tinnitus over time with detailed analytics.'
    },
    {
      icon: UserGroupIcon,
      title: 'Expert-Backed',
      description: 'Built with input from audiologists and tinnitus specialists.'
    },
    {
      icon: ShieldCheckIcon,
      title: 'Privacy-First',
      description: 'Your health data is secure and always under your control.'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-auris-grey to-white">
      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center">
          <div className="flex justify-center mb-8">
            <div className="w-20 h-20 bg-auris-teal rounded-2xl flex items-center justify-center">
              <SpeakerWaveIcon className="w-10 h-10 text-white" />
            </div>
          </div>
          
          <h1 className="text-4xl md:text-6xl font-bold text-auris-dark mb-6">
            TinniTrack by Auris
          </h1>
          
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            Take control of your tinnitus journey with our comprehensive self-assessment 
            and tracking platform. Monitor your progress and explore treatment options 
            backed by clinical research.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/assessment"
              className="bg-auris-teal text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-opacity-90 transition-colors"
            >
              Start Assessment
            </Link>
            <Link
              to="/questionnaire"
              className="bg-auris-blue text-auris-dark px-8 py-4 rounded-lg text-lg font-semibold hover:bg-opacity-80 transition-colors"
            >
              Take Questionnaire
            </Link>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-auris-dark mb-4">
              Comprehensive Tinnitus Management
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Our platform provides everything you need to understand, track, and manage your tinnitus.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Link
                  key={index}
                  to={feature.link}
                  className="group bg-auris-grey rounded-xl p-8 hover:shadow-lg transition-all duration-300 hover:bg-auris-blue hover:bg-opacity-20"
                >
                  <div className="w-12 h-12 bg-auris-teal rounded-lg flex items-center justify-center mb-6 group-hover:bg-opacity-90 transition-colors">
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-auris-dark mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 group-hover:text-auris-dark transition-colors">
                    {feature.description}
                  </p>
                </Link>
              );
            })}
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="bg-auris-blue bg-opacity-10 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-auris-dark mb-4">
              Why Choose TinniTrack?
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Built with your needs in mind, combining cutting-edge technology with clinical expertise.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-6 shadow-sm">
                    <Icon className="w-8 h-8 text-auris-teal" />
                  </div>
                  <h3 className="text-xl font-semibold text-auris-dark mb-3">
                    {benefit.title}
                  </h3>
                  <p className="text-gray-600">
                    {benefit.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-auris-teal py-16">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to Take Control?
          </h2>
          <p className="text-xl text-white text-opacity-90 mb-8">
            Start your tinnitus management journey today. It's free to begin.
          </p>
          <Link
            to="/assessment"
            className="bg-white text-auris-teal px-8 py-4 rounded-lg text-lg font-semibold hover:bg-opacity-90 transition-colors inline-block"
          >
            Get Started Now
          </Link>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="bg-yellow-50 border-t border-yellow-200 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm text-yellow-800">
            <strong>Medical Disclaimer:</strong> TinniTrack is for informational purposes only and is not a substitute for professional medical advice, diagnosis, or treatment. Always seek the advice of your physician or other qualified health provider with any questions you may have regarding a medical condition.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Landing;
